import React, { useState, useRef, useEffect, useId } from 'react';
import './CustomDropdown.css';
import { ChevronDown, ChevronUp } from 'lucide-react';

const CustomDropdown = ({
    options,
    value,
    onChange,
    placeholder = 'Select an option',
    className = '',
    disabled = false,
    label,
    ariaLabel,
    /** When set, used as the trigger button id (for external <label htmlFor>). */
    id: idProp,
    /** id of an external label element (mutually exclusive with using `label` prop). */
    ariaLabelledBy,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const baseId = useId();
    const internalLabelId = `${baseId}-label`;
    const triggerId = idProp ?? `${baseId}-trigger`;
    const listboxId = `${triggerId}-listbox`;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSelect = (optionValue) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    const selectedOption = options.find(option => option.value === value);
    const displayText = selectedOption ? selectedOption.label : placeholder;

    return (
        <div className={`custom-dropdown-container ${className}`.trim()}>
            {label && (
                <label id={internalLabelId} className="custom-dropdown-label" htmlFor={triggerId}>
                    {label}
                </label>
            )}
            <div
                className={`custom-dropdown ${disabled ? 'custom-dropdown--disabled' : ''}`}
                ref={dropdownRef}
            >
                <button
                    type="button"
                    id={triggerId}
                    className={`dropdown-header ${isOpen ? 'open' : ''}`}
                    disabled={disabled}
                    onClick={() => !disabled && setIsOpen((o) => !o)}
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    aria-controls={isOpen ? listboxId : undefined}
                    aria-labelledby={label ? internalLabelId : ariaLabelledBy || undefined}
                    aria-label={!label && !ariaLabelledBy ? (ariaLabel || placeholder) : undefined}
                >
                    <span className="dropdown-selected">{displayText}</span>
                    <span className="dropdown-arrow" aria-hidden>
                        {isOpen ? <ChevronUp size={18} strokeWidth={2} /> : <ChevronDown size={18} strokeWidth={2} />}
                    </span>
                </button>

                {isOpen && (
                    <div
                        id={listboxId}
                        className="dropdown-options"
                        role="listbox"
                        aria-labelledby={label ? internalLabelId : ariaLabelledBy || triggerId}
                    >
                        {options.map((option) => (
                            <div
                                key={option.value}
                                role="option"
                                aria-selected={option.value === value}
                                className={`dropdown-option ${option.value === value ? 'selected' : ''}`}
                                onClick={() => handleSelect(option.value)}
                            >
                                {option.label}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomDropdown;
